import { useCallback, useEffect, useMemo, useState } from 'react'

import { type MetadataContent } from '@redteclab/api/clients/adserver-api'

import {
  FEATURE_TOGGLE_AD_SERVER,
  useFeatureToggle,
} from '../../../feature-toggles'
import {
  AD_SERVER_ON_PLACED_EVENT,
  AD_SERVER_ON_PLACEMENTS_COUNT_SET_EVENT,
  AD_SERVER_ON_REPLACED_EVENT,
  adServerSlotsGetAutoRefresh,
  adServerSlotsGetBannerSize,
  adServerSlotsGetElements,
  adServerSlotsGetTags,
} from '../../adServerSlots'
import { type BannerData, type BannerProps } from '../../model/adServerTypes'

export type AdServerState = {
  getBanner: ({ height, id, width }: BannerProps) => BannerData | undefined
}

const getBannerId = ({ height, id, width }: BannerProps): string => {
  return `${id}_${width}_${height}`
}

export const useAdServerState = (): AdServerState => {
  const [banners, setBanners] = useState<Record<string, BannerData>>({})
  const isAdServerEnabled = useFeatureToggle(FEATURE_TOGGLE_AD_SERVER)

  const updateBannerData = useCallback(
    (
      slot: HTMLElement,
      key: keyof BannerData,
      value: BannerData[keyof BannerData],
    ) => {
      const id = adServerSlotsGetTags(slot)
      const dimensions = adServerSlotsGetBannerSize(slot)

      if (!id || !dimensions) {
        return
      }

      const bannerId = getBannerId({
        height: dimensions.height,
        id,
        width: dimensions.width,
      })

      setBanners((prev) => ({
        ...prev,
        [bannerId]: {
          ...prev[bannerId],
          [key]: value,
        },
      }))
    },
    [],
  )

  useEffect(() => {
    if (!isAdServerEnabled) {
      return
    }

    const slots = adServerSlotsGetElements()

    const adServerOnPlaced = (event: CustomEvent<MetadataContent>): void => {
      const slot: HTMLElement = event.target as HTMLElement
      const dimensions = adServerSlotsGetBannerSize(slot)

      if (dimensions) {
        slot.style.aspectRatio = String(dimensions.width / dimensions.height)
        slot.style.width = `${dimensions.width}px`
        slot.style.maxWidth = '100%'
      }

      slot.dataset.asFlightId = String(event.detail.flightId)

      if (event.detail.customData?.topBannerDisplay) {
        slot.dataset.asTopBanner = event.detail.customData.topBannerDisplay
      }

      updateBannerData(slot, 'metadata', event.detail)
    }

    const adServerOnPlacementsCountSet = (event: CustomEvent<number>): void => {
      updateBannerData(event.target as HTMLElement, 'totalCount', event.detail)
    }

    const adServerOnReplaced = (event: CustomEvent<number>): void => {
      updateBannerData(
        event.target as HTMLElement,
        'currentIndex',
        event.detail,
      )
    }

    for (const slot of slots) {
      const autoRefresh = adServerSlotsGetAutoRefresh(slot)

      slot.addEventListener(AD_SERVER_ON_PLACED_EVENT, adServerOnPlaced, {
        once: !autoRefresh,
      })

      slot.addEventListener(
        AD_SERVER_ON_PLACEMENTS_COUNT_SET_EVENT,
        adServerOnPlacementsCountSet,
        {
          once: true,
        },
      )

      if (autoRefresh) {
        slot.addEventListener(AD_SERVER_ON_REPLACED_EVENT, adServerOnReplaced)
      }
    }

    return (): void => {
      for (const slot of slots) {
        const autoRefresh = adServerSlotsGetAutoRefresh(slot)

        slot.removeEventListener(AD_SERVER_ON_PLACED_EVENT, adServerOnPlaced)
        slot.removeEventListener(
          AD_SERVER_ON_PLACEMENTS_COUNT_SET_EVENT,
          adServerOnPlacementsCountSet,
        )

        if (autoRefresh) {
          slot.removeEventListener(
            AD_SERVER_ON_REPLACED_EVENT,
            adServerOnReplaced,
          )
        }
      }
    }
  }, [isAdServerEnabled, updateBannerData])

  const getBanner = useCallback(
    ({ height, id, width }: BannerProps): BannerData | undefined => {
      const bannerId = getBannerId({ height, id, width })

      return banners[bannerId]
    },
    [banners],
  )

  return useMemo(
    () => ({
      getBanner,
    }),
    [getBanner],
  )
}
